/**
 * useEtfWeights.js — Phase 36 (UI simplification).
 *
 * Owns the per-ETF weight allocation logic for the new SelectionPanels flow.
 *
 *   • Weights are stored as a `{ [isin]: percentage }` map (0-100, NOT decimals).
 *   • Default behaviour: when ETFs are added/removed, all weights are rebalanced
 *     to equal shares.
 *   • The moment the user edits ANY single weight, we flip `manualAllocation`
 *     to true and stop auto-rebalancing. The user is then in charge.
 *   • In manual mode, newly added ETFs get a weight equal to
 *     `(100 - sumOfExistingWeights) / numberOfNewETFs` if that's > 0,
 *     otherwise 0 (the user must manually rebalance).
 *   • In manual mode, removing an ETF leaves the other weights untouched
 *     (the sum may drop below 100; the UI surfaces a warning).
 *
 * Public API:
 *   const {
 *     weights,             // { isin: pct }  current allocation
 *     manualAllocation,    // bool — true once the user has edited anything
 *     totalWeight,         // sum of weights, for warning display
 *     isValid,             // |totalWeight - 100| < 0.01
 *     setWeight(isin, v),  // user edited a single field
 *     resetToEqual(),      // explicit user action: "rebilancia equamente"
 *   } = useEtfWeights(selectedIsins);
 *
 * The hook keeps internal state in sync with `selectedIsins`:
 *   - on ADD: insertion logic depending on manualAllocation
 *   - on REMOVE: drop the entry; keep others untouched in manual mode,
 *                rebalance equally in auto mode
 */
import { useState, useEffect, useRef, useCallback } from "react";


/**
 * Build an equal-weight map for the given ISIN list.
 *
 * Three-share-class style: rounds to 4 decimals to keep displayed totals
 * stable at "100.00%" (rather than "99.9999%").
 */
function equalWeights(isins) {
  const n = isins.length;
  if (n === 0) return {};
  const w = Math.round((100 / n) * 10000) / 10000; // 4-decimal places
  const map = {};
  for (const isin of isins) map[isin] = w;
  // Push the rounding residue onto the last entry so the sum is exactly 100.
  const residue = 100 - w * n;
  if (Math.abs(residue) > 0.00001) {
    map[isins[n - 1]] = Math.round((w + residue) * 10000) / 10000;
  }
  return map;
}


export function useEtfWeights(selectedIsins) {
  const [weights, setWeights] = useState({});
  const [manualAllocation, setManualAllocation] = useState(false);

  // Track the previous ISIN list so we can detect adds vs removes.
  const previousIsinsRef = useRef([]);
  // Set by `loadWeights` so the reconciliation effect — which runs *after*
  // the selection change that comes with a loaded portfolio — does not
  // rebalance the weights we were explicitly asked to restore.
  const pendingLoadRef = useRef(null);

  // Reconciliation on every change to `selectedIsins`. -----------------
  useEffect(() => {
    const prev = previousIsinsRef.current;
    const prevSet = new Set(prev);
    const nextSet = new Set(selectedIsins);

    const added   = selectedIsins.filter((i) => !prevSet.has(i));
    const removed = prev.filter((i) => !nextSet.has(i));

    if (pendingLoadRef.current) {
      setWeights(pendingLoadRef.current);
      pendingLoadRef.current = null;
      previousIsinsRef.current = selectedIsins;
      return;
    }

    if (added.length === 0 && removed.length === 0) {
      previousIsinsRef.current = selectedIsins;
      return;
    }
    setWeights((current) =>
      reconcileWeights({
        previousIsins: prev,
        nextIsins: selectedIsins,
        currentWeights: current,
        manualAllocation,
      })
    );

    previousIsinsRef.current = selectedIsins;
    // We intentionally exclude `manualAllocation` from deps: a mode flip
    // doesn't reconcile by itself, only ADD/REMOVE events do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIsins]);

  // User edited a single weight field. -----------------------------------
  const setWeight = useCallback((isin, value) => {
    // Parse: '' / non-numeric → 0
    const numeric = typeof value === "string"
      ? (value.trim() === "" ? 0 : parseFloat(value))
      : Number(value);
    const safe = Number.isFinite(numeric) ? Math.max(0, numeric) : 0;
    setWeights((current) => ({ ...current, [isin]: safe }));
    if (!manualAllocation) setManualAllocation(true);
  }, [manualAllocation]);

  // Explicit reset action: "ribilancia equamente" button. ----------------
  const resetToEqual = useCallback(() => {
    setWeights(equalWeights(selectedIsins));
    setManualAllocation(false);
  }, [selectedIsins]);

  // Restore a saved portfolio's weights (Release 2). ---------------------
  const loadWeights = useCallback((map) => {
    pendingLoadRef.current = { ...map };
    setWeights({ ...map });
    setManualAllocation(true);
  }, []);

  // Derived totals -------------------------------------------------------
  const totalWeight = Object.values(weights)
    .reduce((s, v) => s + (Number(v) || 0), 0);
  const isValid = Math.abs(totalWeight - 100) < 0.01;

  return {
    weights,
    manualAllocation,
    totalWeight,
    isValid,
    setWeight,
    resetToEqual,
    loadWeights,
  };
}


// Exposed for unit tests (pure function, no React) --------------------------
export { equalWeights };


// ---------------------------------------------------------------------------
// Pure reconciliation function — extracted for unit-testability.
// Given the previous selection, the new selection, the current weights map,
// and the manualAllocation flag, return the next weights map.
// ---------------------------------------------------------------------------
export function reconcileWeights({
  previousIsins,
  nextIsins,
  currentWeights,
  manualAllocation,
}) {
  const prevSet = new Set(previousIsins);
  const added   = nextIsins.filter((i) => !prevSet.has(i));

  if (!manualAllocation) {
    return equalWeights(nextIsins);
  }

  const out = {};
  for (const isin of nextIsins) {
    if (prevSet.has(isin)) out[isin] = currentWeights[isin] ?? 0;
  }
  if (added.length > 0) {
    const usedTotal = Object.values(out)
      .reduce((s, v) => s + (Number(v) || 0), 0);
    const headroom = Math.max(0, 100 - usedTotal);
    const perNew = headroom > 0
      ? Math.round((headroom / added.length) * 10000) / 10000
      : 0;
    for (const isin of added) out[isin] = perNew;
  }
  return out;
}
